//
// Copyright 2024 DXOS.org
//

import base32Decode from 'base32-decode';
import base32Encode from 'base32-encode';
import * as Schema from 'effect/Schema';

import { invariant } from '@dxos/invariant';

import { randomBytes } from './random-bytes.ts';
import { withStatics } from './schema-statics.ts';

/**
 * Denotes RFC4648 base-32 format.
 */
const MULTIBASE_PREFIX = 'B';

const DID_PREFIX = 'did:halo:';

const DECODED_BYTE_LENGTH = 20;

const ENCODED_LENGTH = 42;

/**
 * RFC4648 base-32 alphabet (uppercase A–Z and digits 2–7).
 */
const RFC4648_BASE32_PATTERN = /^[A-Z2-7]+$/;

const isValid = (value: unknown): value is IdentityDid => {
  if (
    typeof value !== 'string' ||
    !value.startsWith(DID_PREFIX + MULTIBASE_PREFIX) ||
    value.length !== ENCODED_LENGTH
  ) {
    return false;
  }

  const encoded = value.slice(DID_PREFIX.length + MULTIBASE_PREFIX.length);
  if (!RFC4648_BASE32_PATTERN.test(encoded)) {
    return false;
  }

  try {
    // Reject inputs that pass the prefix/length check but are not decodable to the expected byte length.
    return base32Decode(encoded, 'RFC4648').byteLength === DECODED_BYTE_LENGTH;
  } catch {
    return false;
  }
};

/**
 * A unique identifier for an identity.
 * Identity DIDs are generated by creating a keypair, and then taking the first 20 bytes of the SHA-256 hash of the public key and encoding them to multibase RFC4648 base-32 format (prefixed with B, see Multibase Table).
 * @example did:halo:BA25QRC2FEWCSAMRP4RZL65LWJ7352CKE
 */
export type IdentityDid = string & { __IdentityDid: never };

const IdentityDidSchema = Schema.String.pipe(Schema.refine((value): value is IdentityDid => isValid(value)));

// Effect 4 schemas are values rather than extensible classes, so the statics are merged onto the
// schema instead of declared on a subclass.
export const IdentityDid: Schema.Codec<IdentityDid, string> & {
  byteLength: number;
  encode: (value: Uint8Array) => IdentityDid;
  decode: (value: IdentityDid) => Uint8Array;
  isValid: (value: unknown) => value is IdentityDid;
  make: (value: string) => IdentityDid;
  random: () => IdentityDid;
} = withStatics(IdentityDidSchema, {
  byteLength: DECODED_BYTE_LENGTH,

  encode: (value: Uint8Array): IdentityDid => {
    invariant(value instanceof Uint8Array, 'Invalid type');
    invariant(value.length === IdentityDid.byteLength, 'Invalid length');
    return (DID_PREFIX + MULTIBASE_PREFIX + base32Encode(value, 'RFC4648')) as IdentityDid;
  },

  decode: (value: IdentityDid): Uint8Array => {
    invariant(value.startsWith(DID_PREFIX + MULTIBASE_PREFIX), 'Invalid multibase32 encoding');
    return new Uint8Array(base32Decode(value.slice(DID_PREFIX.length + MULTIBASE_PREFIX.length), 'RFC4648'));
  },

  isValid,

  make: (value: string): IdentityDid => {
    invariant(isValid(value), 'Invalid IdentityDid');
    return value;
  },

  random: (): IdentityDid => IdentityDid.encode(randomBytes(IdentityDid.byteLength)),
});
